import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Тост: короткое подтверждение того, что действие прошло (кит, «Тост»).
 *
 * - Живёт 4 секунды, при наведении не исчезает; одновременно один.
 * - «Отменить» — только там, где отмена технически возможна. Тогда тост
 *   живёт 7 секунд (ТЗ M2 §8), а сама отмена — обратная операция
 *   на сервере, её передаёт вызывающий.
 *
 * Тексты отказов сюда приходят **с сервера** и показываются как есть
 * (ТЗ §5) — свои формулировки бизнес-правил на фронтенде не пишутся.
 */

const PLAIN_MS = 4000;
const UNDO_MS = 7000;

interface ToastState {
  id: number;
  text: string;
  tone?: 'crit';
  onUndo?: () => void;
}

interface ToastApi {
  show: (text: string, options?: { onUndo?: () => void }) => void;
  showError: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const seq = useRef(0);

  const stop = useCallback(() => window.clearTimeout(timer.current), []);

  const start = useCallback(
    (next: ToastState) => {
      stop();
      timer.current = window.setTimeout(
        () => setToast((t) => (t?.id === next.id ? null : t)),
        next.onUndo ? UNDO_MS : PLAIN_MS,
      );
    },
    [stop],
  );

  const push = useCallback(
    (next: Omit<ToastState, 'id'>) => {
      const withId = { ...next, id: ++seq.current };
      setToast(withId);
      start(withId);
    },
    [start],
  );

  useEffect(() => stop, [stop]);

  const api = useMemo<ToastApi>(
    () => ({
      show: (text, options) => push({ text, onUndo: options?.onUndo }),
      showError: (text) => push({ text, tone: 'crit' }),
    }),
    [push],
  );

  const undo = () => {
    if (!toast?.onUndo) return;
    stop();
    const run = toast.onUndo;
    setToast(null);
    run();
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast ? (
        <div
          className={toast.tone ? 'toast crit' : 'toast'}
          role="status"
          onMouseEnter={stop}
          onMouseLeave={() => start(toast)}
        >
          <span>{toast.text}</span>
          {toast.onUndo ? (
            <span className="undo" onClick={undo}>
              Отменить
            </span>
          ) : null}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast вне ToastProvider');
  return api;
}
